import PropTypes from 'prop-types'
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { Button } from './Button'
import { Menu } from './Menu'

export function Pagination({
  currentPage,
  rate,
  total,
  setCurrentPage,
  setRate,
  userId,
  tableId,
  sizes = [20, 50, 100],
  small = false,
  failureLabel = window.MEButtons.text('strPageFailed'),
  onFailure,
  label = window.MEButtons.text('strPagination'),
  busy = false,
  blocked = false,
  failureMessage,
}) {
  const pages = Math.max(1, Math.ceil(total / rate))
  const current = Math.max(1, Math.min(currentPage, pages))
  const [draft, setDraft] = useState(String(current))
  const [pending, setPending] = useState(false)
  // The pager's own request, or one the consumer owns, holds every control.
  const loading = pending || busy
  const locked = loading || blocked
  const action = useRef(null)
  // Each action's running page request. StrictMode replays the effects, so a
  // disposed action can still hold one.
  const sizing = useRef(new Map())
  const failure = useRef(onFailure)
  failure.current = onFailure
  // Bumped to rerun the restore once the table's running requests end without
  // saving a size; a saved size reaches this pager through the rate prop.
  const [restores, retryRestore] = useState(0)
  const message = useRef(failureMessage)
  message.current = failureMessage
  const root = useRef(null)
  const heldFocus = useRef(false)
  const rangeId = useId()
  useEffect(() => {
    setDraft(String(current))
  }, [current])
  useEffect(() => {
    const { announce, createActionState } = window.MEButtons
    const created = createActionState(
      state => setPending(state.pending),
      announce,
      error => {
        sizing.current.get(created)?.release()
        sizing.current.delete(created)
        failure.current?.(error)
      },
      commit => {
        sizing.current.delete(created)
        commit?.()
      }
    )
    action.current = created
    return () => created.dispose()
  }, [])
  // React runs layout-effect cleanups before it removes an unmounting
  // component's nodes, so the pager can still see whether it holds focus.
  useLayoutEffect(
    () => () => {
      heldFocus.current = !!root.current?.contains(document.activeElement)
    },
    []
  )
  // React runs passive-effect cleanups after that commit, once the
  // replacement's ref is attached. The focus tests fail if either order changes.
  useEffect(
    () => () => {
      const target = message.current?.current
      if (
        heldFocus.current &&
        target?.isConnected &&
        (!document.activeElement || document.activeElement === document.body)
      )
        target.focus()
    },
    []
  )
  const load = (next, size) => {
    if (action.current.pending || busy || blocked || size === rate) return
    const { pageSizeCommit, pagePreference } = window.MEButtons
    const key = setRate ? pagePreference(userId, tableId) : null
    const commit = key ? pageSizeCommit(key, size) : null
    if (commit) sizing.current.set(action.current, commit)
    // The request starts in the activating event, as Button's onAction does,
    // so a consumer that commits at once updates in that event.
    let request
    try {
      request = Promise.resolve(
        size === undefined ? setCurrentPage(next) : setRate(size)
      ).then(() => commit)
    } catch (error) {
      request = Promise.reject(error)
    }
    action.current.run(request, {
      load: 'none',
      busyLabel: window.MEButtons.text('strLoadingPage', { page: next }),
      failureLabel:
        size !== undefined
          ? window.MEButtons.text('strPageSizeFailed')
          : failureLabel,
    })
  }
  useEffect(() => {
    if (!setRate) return
    const { pagePreference, pageSizePreference, pageSizeRunning } =
      window.MEButtons
    const key = pagePreference(userId, tableId)
    // A request this pager started itself (StrictMode reruns this effect)
    // does not hold the restore.
    if (
      !sizing.current.size &&
      pageSizeRunning(key, saved => saved || retryRestore(n => n + 1))
    )
      return
    const saved = Number(pageSizePreference(key))
    if (sizes.includes(saved) && saved !== rate) load(1, saved)
  }, [userId, tableId, restores])
  const jump = value => {
    setDraft(String(current))
    const next = window.MEButtons.pageNumber(value, pages)
    if (next !== null && next !== current) load(next)
  }
  const navigation = (
    <nav
      className={`me-btn-pagination${small ? ' me-btn-pagination-sm' : ''}${
        loading ? ' me-pg-loading' : ''
      }`}
      aria-label={label}
      aria-busy={loading || undefined}
      data-me-react=""
      ref={root}
    >
      <Button
        className="me-btn-pagination-page"
        data-page="prev"
        aria-label={window.MEButtons.text('strPreviousPage')}
        aria-disabled={locked || current === 1}
        icon="fa-regular fa-chevron-left"
        onClick={() => jump(current - 1)}
      />
      <span className="me-pg-field">
        <input
          className="me-pg-input"
          aria-label={window.MEButtons.text('strPage')}
          aria-describedby={rangeId}
          data-page="jump"
          inputMode="numeric"
          value={draft}
          readOnly={locked}
          aria-disabled={locked || undefined}
          onChange={event => setDraft(event.target.value)}
          onBlur={() => setDraft(String(current))}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault()
              jump(draft)
            }
            if (event.key === 'Escape') {
              event.preventDefault()
              event.stopPropagation()
              setDraft(String(current))
            }
          }}
        />
        <span className="me-pg-of" id={rangeId}>
          {window.MEButtons.text('strPageOf', { pages })}
        </span>
      </span>
      <Button
        className="me-btn-pagination-page"
        data-page="next"
        aria-label={window.MEButtons.text('strNextPage')}
        aria-disabled={locked || current === pages}
        icon="fa-regular fa-chevron-right"
        onClick={() => jump(current + 1)}
      />
    </nav>
  )
  if (!setRate) return navigation
  const start = total ? (current - 1) * rate + 1 : 0
  const end = Math.min(total, current * rate)
  return (
    <div className="me-pagination-layout" data-me-react="">
      <div className="me-pg-footer">
        <div className="me-pg-size">
          <Menu
            label={window.MEButtons.text('strPageSize')}
            valueLabel={window.MEButtons.text('strPerPage', { size: rate })}
            aria-disabled={locked || undefined}
          >
            {sizes.map(size => (
              <Button
                key={size}
                className="me-menu-item"
                data-page-size={size}
                onClick={() => load(1, size)}
              >
                {window.MEButtons.text('strPerPage', { size })}
              </Button>
            ))}
          </Menu>
        </div>
        <span className="me-pg-info" aria-live="polite">
          {window.MEButtons.text('strShowing', { start, end, total })}
        </span>
      </div>
      {navigation}
    </div>
  )
}

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  rate: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  // Receives the requested page and returns the request's promise.
  setCurrentPage: PropTypes.func.isRequired,
  small: PropTypes.bool,
  failureLabel: PropTypes.string,
  onFailure: PropTypes.func,
  // The landmark's name, distinct for each pager on a page that shows more
  // than one.
  label: PropTypes.string,
  // A request the consumer owns is loading the rows: the pager reads busy and
  // holds its controls in place, focusable and blocked.
  busy: PropTypes.bool,
  // The consumer cannot page right now, as after its load failed: the controls
  // stay in place, focusable and blocked, without reading busy.
  blocked: PropTypes.bool,
  // A ref to the message a consumer renders in the pager's place when a page
  // fails to load, focusable through tabIndex -1. A pager removed while it
  // holds focus moves focus there.
  failureMessage: PropTypes.object,
  // Receives the requested page size and returns the request's promise; the
  // consumer shows page 1 at the requested size. Supplying it adds the footer.
  setRate: PropTypes.func,
  userId: requiredWithFooter(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  tableId: requiredWithFooter(PropTypes.string),
  sizes: ascendingSizes,
}

function requiredWithFooter(type) {
  return (props, ...args) =>
    (props.setRate ? type.isRequired : type)(props, ...args)
}

function ascendingSizes(props, name) {
  const sizes = props[name]
  if (sizes === undefined) return null
  const valid =
    Array.isArray(sizes) &&
    sizes.length > 0 &&
    sizes.every(
      (size, index) =>
        Number.isInteger(size) &&
        size > 0 &&
        (index === 0 || size > sizes[index - 1])
    )
  return valid
    ? null
    : new Error(`${name} must be ascending positive integers.`)
}
